import { useState, useEffect, useRef } from "react";
import { uid, T, mono, MONTH_NAMES, CALENDAR_COLORS, COUNTRY_CODES } from "./utils";
import { supabase } from "./supabase";

const EVENT_TYPES = ["Shoot","Artist Residency","Festival","Meeting","Travel","Other"];
const DEFAULT_CITY = "Berlin";
const CITY_COLORS = { "Berlin":"#4a6fa5", "İstanbul":"#2d7a2d", "Seyahatte":"#e17055" };
const CITY_PRESETS = ["Berlin","İstanbul","Atina","Diğer"];
function cityColor(city){ return CITY_COLORS[city] || "#9b7ec8"; }

function daysInMonth(y,m) { return new Date(y,m+1,0).getDate(); }
function dayOfYear(y,m,d) { let x=0; for(let i=0;i<m;i++) x+=daysInMonth(y,i); return x+d; }
function totalDays(y) { return (y%4===0&&(y%100!==0||y%400===0))?366:365; }

async function syncToGCal(event, googleToken, year) {
  if (!googleToken) return;
  try {
    const startDate = new Date(year, event.startMonth, event.startDay);
    const endDate = new Date(year, event.endMonth, event.endDay + 1);
    await fetch("https://www.googleapis.com/calendar/v3/calendars/primary/events", {
      method: "POST",
      headers: { Authorization: `Bearer ${googleToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        summary: `${event.type}: ${event.title}${event.country ? ` [${event.country}]` : ""}`,
        description: event.notes || "",
        start: { date: startDate.toISOString().slice(0,10) },
        end: { date: endDate.toISOString().slice(0,10) },
      }),
    });
  } catch (e) { console.error("GCal sync error:", e); }
}

export function CalendarView({ googleToken }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:"", type:"Shoot", startMonth:now.getMonth(), startDay:1, endMonth:now.getMonth(), endDay:7, color:CALENDAR_COLORS[0], notes:"", country:"" });
  const [selEvent, setSelEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [selectedDay, setSelectedDay] = useState(null); // {month, day}
  const [locations, setLocations] = useState([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({ startMonth: now.getMonth(), startDay: 1, endMonth: now.getMonth(), endDay: 7, city: "Berlin" });
  const [yearNote, setYearNote] = useState("");
  const [travels, setTravels] = useState([]); // seyahat günleri için hesaplama amaçlı, artık PDF panel yok

  useEffect(() => { load(); loadLocations(); loadYearNote(); loadTravelsForStats(); }, [year]); // eslint-disable-line

  async function loadTravelsForStats() {
    const { data } = await supabase.from("travels").select("*").order("travel_date");
    setTravels(data||[]);
  }

  async function loadYearNote() {
    const { data } = await supabase.from("app_settings").select("value").eq("key",`year_note_${year}`).maybeSingle();
    setYearNote(data?.value?.note || "");
  }

  const noteTimer = useRef();
  function saveYearNote(note) {
    setYearNote(note);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      await supabase.from("app_settings").upsert({ key:`year_note_${year}`, value:{ note } }, { onConflict:"key" });
    }, 600);
  }

  async function loadLocations() {
    const { data, error } = await supabase.from("calendar_locations").select("*").eq("year", year).order("start_month");
    if (error) { console.error("calendar_locations okunamadı:", error.message); setLocations([]); return; }
    setLocations(data||[]);
  }

  async function addLocation() {
    if (!newLocation.city.trim()) return;
    const loc = { id:uid(), year, start_month:newLocation.startMonth, start_day:newLocation.startDay, end_month:newLocation.endMonth, end_day:newLocation.endDay, city:newLocation.city.trim(), color:cityColor(newLocation.city.trim()) };
    setLocations(prev=>[...prev, loc]);
    const { error } = await supabase.from("calendar_locations").insert(loc);
    if (error) {
      alert("Konum kaydedilemedi: " + error.message + "\n\nSupabase'de 'calendar_locations' tablosu eksik olabilir.");
      setLocations(prev=>prev.filter(l=>l.id!==loc.id));
      return;
    }
    setShowLocationForm(false);
  }

  async function removeLocation(id) {
    setLocations(prev=>prev.filter(l=>l.id!==id));
    await supabase.from("calendar_locations").delete().eq("id",id);
  }

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("calendar_events").select("*").eq("year", year).neq("type","reminder").order("start_month");
    setEvents((data||[]).map(e => ({
      id:e.id, title:e.title, type:e.type,
      startMonth:e.start_month, startDay:e.start_day,
      endMonth:e.end_month, endDay:e.end_day,
      color:e.color, notes:e.notes||"", year:e.year,
      country:e.country||"",
    })));
    setLoading(false);
  }

  async function addEvent() {
    if (!form.title) return;
    const ev = { id:uid(), ...form, year };
    setEvents(e => [...e, ev]);
    setShowForm(false);
    setForm(f => ({ ...f, title:"", notes:"", country:"" }));
    await supabase.from("calendar_events").insert({
      id:ev.id, title:ev.title, type:ev.type,
      start_month:ev.startMonth, start_day:ev.startDay,
      end_month:ev.endMonth, end_day:ev.endDay,
      color:ev.color, notes:ev.notes, year:ev.year, country:ev.country||"",
    });
    if (googleToken) {
      await syncToGCal(ev, googleToken, year);
      setSyncMsg("Google Calendar'a eklendi ✓");
      setTimeout(() => setSyncMsg(""), 3000);
    }
  }

  async function saveEdit() {
    if (!editingEvent) return;
    setEvents(e => e.map(x => x.id===editingEvent.id ? editingEvent : x));
    await supabase.from("calendar_events").update({
      title:editingEvent.title, type:editingEvent.type,
      start_month:editingEvent.startMonth, start_day:editingEvent.startDay,
      end_month:editingEvent.endMonth, end_day:editingEvent.endDay,
      color:editingEvent.color, notes:editingEvent.notes,
      country:editingEvent.country||"",
    }).eq("id", editingEvent.id);
    setEditingEvent(null);
    setSelEvent(editingEvent.id);
  }

  async function syncAll() {
    if (!googleToken || events.length===0) return;
    setSyncing(true);
    for (const ev of events) await syncToGCal(ev, googleToken, year);
    setSyncing(false);
    setSyncMsg(`${events.length} etkinlik Google Calendar'a aktarıldı ✓`);
    setTimeout(() => setSyncMsg(""), 4000);
  }

  async function removeEvent(id) {
    setEvents(e => e.filter(x => x.id!==id));
    if (selEvent===id) setSelEvent(null);
    if (editingEvent?.id===id) setEditingEvent(null);
    await supabase.from("calendar_events").delete().eq("id", id);
  }

  const total = totalDays(year);
  const selEv = editingEvent || events.find(e => e.id===selEvent);

  // ── Konum istatistikleri: her gün için şehir ata ──
  function cityForDay(doy) {
    // 1) Manuel eklenen konumlar en yüksek öncelik
    for (const loc of locations) {
      const s = dayOfYear(year, loc.start_month, loc.start_day);
      const e = dayOfYear(year, loc.end_month, loc.end_day);
      if (doy >= s && doy <= e) return loc.city;
    }
    // 2) Ülkesi girilmiş etkinlikler — Türkiye ise İstanbul, değilse Seyahatte
    for (const ev of events) {
      if (!ev.country) continue;
      const s = dayOfYear(year, ev.startMonth, ev.startDay);
      const e = dayOfYear(year, ev.endMonth, ev.endDay);
      if (doy >= s && doy <= e) {
        const c = ev.country.trim().toLowerCase();
        if (c === "türkiye" || c === "turkiye" || c === "turkey") return "İstanbul";
        return "Seyahatte";
      }
    }
    // 3) Seyahat günleri (uçuş tarihleri)
    for (const tr of travels) {
      const flights = tr.flights || [];
      if (flights.length === 0) continue;
      const dates = flights.map(f=>f.date).filter(Boolean).sort();
      if (dates.length === 0) continue;
      const first = new Date(dates[0]);
      const last = new Date(dates[dates.length-1]);
      if (first.getFullYear() !== year && last.getFullYear() !== year) continue;
      const cur = new Date(year, 0, doy);
      if (cur >= first && cur <= last) return "Seyahatte";
    }
    return DEFAULT_CITY;
  }

  const cityTally = {};
  for (let d = 1; d <= total; d++) {
    const c = cityForDay(d);
    cityTally[c] = (cityTally[c]||0) + 1;
  }
  const cityStats = Object.entries(cityTally).sort((a,b)=>b[1]-a[1]);

  // ── Ay ızgarası için event overlap hesaplama ──
  function eventsForDay(mi, day) {
    return events.filter(ev => {
      const s = dayOfYear(year, ev.startMonth, ev.startDay);
      const e = dayOfYear(year, ev.endMonth, ev.endDay);
      const d = dayOfYear(year, mi, day);
      return d >= s && d <= e;
    });
  }

  const selectedDayEvents = selectedDay ? eventsForDay(selectedDay.month, selectedDay.day) : [];
  const selectedDayCountries = [...new Set(selectedDayEvents.map(e=>e.country).filter(Boolean))];
  const hasConflict = selectedDayEvents.length >= 2 && selectedDayCountries.length >= 2;

  if (loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  return (
    <div style={{flex:1,overflowY:"auto",padding:"28px 32px"}}>
      <style>{`
        .cal-year-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;}
        @media(max-width:900px){.cal-year-grid{grid-template-columns:repeat(2,1fr)!important;}}
        @media(max-width:520px){.cal-year-grid{grid-template-columns:1fr!important;}}
      `}</style>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"baseline",gap:10}}>
          <button onClick={()=>setYear(y=>y-1)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 9px",cursor:"pointer",fontSize:12,color:T.mute}}>←</button>
          <span style={{fontSize:24,fontWeight:600,letterSpacing:"-0.02em"}}>{year}</span>
          <button onClick={()=>setYear(y=>y+1)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 9px",cursor:"pointer",fontSize:12,color:T.mute}}>→</button>
        </div>
        <span style={{...mono,fontSize:10,color:T.mute}}>{events.length} ETKİNLİK · {travels.length} SEYAHAT</span>
      </div>

      {/* Action bar */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
        <button onClick={()=>{setShowForm(s=>!s);setEditingEvent(null);setSelEvent(null);}} style={{...mono,fontSize:10,background:T.ink,color:"#fff",border:"none",borderRadius:2,padding:"6px 14px",cursor:"pointer",fontWeight:500}}>{showForm?"× İptal":"+ Etkinlik Ekle"}</button>
        {googleToken&&(
          <button onClick={syncAll} disabled={syncing} style={{...mono,fontSize:10,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"6px 12px",cursor:"pointer",color:T.mute}}>
            {syncing?"Syncing…":"→ Google Calendar"}
          </button>
        )}
        {syncMsg&&<span style={{...mono,fontSize:9,color:"#2d7a2d",alignSelf:"center"}}>{syncMsg}</span>}
      </div>

      {showForm&&(
        <EventForm form={form} setForm={setForm} onSave={addEvent} onCancel={()=>setShowForm(false)} title="Yeni Etkinlik" googleToken={googleToken}/>
      )}

      {/* 12 aylık tam yıl ızgarası */}
      <div className="cal-year-grid" style={{marginBottom:20}}>
        {MONTH_NAMES.map((mname,mi)=>{
          const days=daysInMonth(year,mi);
          const firstDow=(new Date(year,mi,1).getDay()+6)%7;
          const mevs=events.filter(ev=>{const es=ev.startMonth*100+ev.startDay;const ee=ev.endMonth*100+ev.endDay;return es<=mi*100+days&&ee>=mi*100+1;});
          const isCurMonth = now.getFullYear()===year && now.getMonth()===mi;
          return (
            <div key={mi} style={{border:isCurMonth?`1.5px solid ${T.ink}`:`0.5px solid ${T.border}`,borderRadius:8,padding:"10px",background:isCurMonth?"#fff":"transparent"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:isCurMonth?700:600,color:T.ink}}>{mname.slice(0,3).toUpperCase()}</span>
                {mevs.length>0&&<span style={{...mono,fontSize:8,color:mevs[0].color}}>{mevs.length}e</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
                {["P","S","Ç","P","C","C","P"].map((d,i)=><div key={i} style={{...mono,fontSize:6,color:T.border,textAlign:"center"}}>{d}</div>)}
                {Array.from({length:firstDow}).map((_,i)=><div key={"e"+i}/>)}
                {Array.from({length:days},(_,i)=>i+1).map(day=>{
                  const dayEvs=mevs.filter(ev=>{const sd=ev.startMonth===mi?ev.startDay:1;const ed=ev.endMonth===mi?ev.endDay:days;return day>=sd&&day<=ed;});
                  const isToday=now.getFullYear()===year && now.getMonth()===mi && now.getDate()===day;
                  const isSelected = selectedDay?.month===mi && selectedDay?.day===day;
                  const doy = dayOfYear(year, mi, day);
                  const dayLoc = locations.find(loc=>{
                    const s = dayOfYear(year, loc.start_month, loc.start_day);
                    const e = dayOfYear(year, loc.end_month, loc.end_day);
                    return doy>=s && doy<=e;
                  });
                  let bg = "transparent", color = T.ink;
                  if (isToday) { bg = T.ink; color = "#fff"; }
                  else if (dayEvs.length===1) { bg = dayEvs[0].color+"22"; color = dayEvs[0].color; }
                  else if (dayEvs.length>=2) { bg = `linear-gradient(135deg,${dayEvs[0].color}33 50%,${dayEvs[1].color}33 50%)`; color = T.ink; }
                  else if (dayLoc) { bg = cityColor(dayLoc.city)+"22"; color = cityColor(dayLoc.city); }
                  return (
                    <div key={day} onClick={()=>setSelectedDay({month:mi,day})}
                      style={{
                        textAlign:"center",fontSize:8,padding:"2px 0",borderRadius:2,cursor:"pointer",
                        background:bg,color,fontWeight:isToday||dayEvs.length>0?700:400,
                        outline:isSelected?`1.5px solid ${T.accent}`:"none",outlineOffset:-1,
                      }}>{day}</div>
                  );
                })}
              </div>
              {mevs.length>0&&<div style={{marginTop:6,display:"flex",flexDirection:"column",gap:2}}>
                {mevs.map(ev=><div key={ev.id} onClick={()=>{setSelEvent(ev.id);setEditingEvent(null);}} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer"}}>
                  <span style={{width:4,height:4,borderRadius:1,background:ev.color,flexShrink:0}}/>
                  <span style={{fontSize:8,color:T.mute,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.title}{ev.country?` · ${ev.country}`:""}</span>
                </div>)}
              </div>}
            </div>
          );
        })}
      </div>

      {/* Gün detay paneli — bir güne tıklanınca */}
      {selectedDay && (
        <div style={{border:`0.5px solid ${T.border}`,borderRadius:10,padding:"14px 16px",marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10}}>
            <span style={{...mono,fontSize:9,color:T.mute,letterSpacing:"0.08em"}}>
              {selectedDay.day} {MONTH_NAMES[selectedDay.month].toUpperCase()} — {selectedDayEvents.length} ETKİNLİK
            </span>
            <button onClick={()=>setSelectedDay(null)} style={{background:"none",border:"none",color:T.mute,fontSize:14,cursor:"pointer",padding:0}}>×</button>
          </div>
          {selectedDayEvents.length===0?(
            <div style={{...mono,fontSize:10,color:T.border}}>Bu gün için etkinlik yok.</div>
          ):selectedDayEvents.map(ev=>(
            <div key={ev.id} onClick={()=>{setSelEvent(ev.id);setEditingEvent(null);}} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 0",borderBottom:`0.5px solid ${T.borderLight}`,cursor:"pointer"}}>
              <span style={{width:8,height:8,borderRadius:2,background:ev.color,flexShrink:0,marginTop:3}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:T.ink,fontWeight:500}}>{ev.title}</div>
                <div style={{...mono,fontSize:9,color:T.mute,marginTop:2}}>
                  {ev.type} · {MONTH_NAMES[ev.startMonth].slice(0,3).toUpperCase()} {ev.startDay} — {MONTH_NAMES[ev.endMonth].slice(0,3).toUpperCase()} {ev.endDay}{ev.country?` · ${ev.country}`:""}
                </div>
              </div>
            </div>
          ))}
          {hasConflict&&(
            <div style={{...mono,fontSize:9,color:"#c0392b",marginTop:10,padding:"7px 10px",background:"#c0392b0d",borderRadius:6}}>
              ⚠ Aynı tarihte {selectedDayCountries.join(" / ")} — seyahat çakışması olabilir
            </div>
          )}
        </div>
      )}

      {/* Seçili etkinlik detay/edit — aynı konumda açılır, sayfa yukarı zıplamaz */}
      {editingEvent&&(
        <EventForm form={editingEvent} setForm={setEditingEvent} onSave={saveEdit} onCancel={()=>setEditingEvent(null)} title="Etkinliği Düzenle" googleToken={googleToken}/>
      )}
      {selEv&&!editingEvent&&(
        <div style={{marginBottom:20,border:`1.5px solid ${selEv.color}`,borderRadius:8,padding:"16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{...mono,fontSize:9,color:selEv.color,marginBottom:6}}>{selEv.type.toUpperCase()}{selEv.country?` · ${selEv.country}`:""}</div>
              <div style={{fontSize:18,fontWeight:600,letterSpacing:"-0.02em",color:T.ink,marginBottom:4}}>{selEv.title}</div>
              <div style={{...mono,fontSize:10,color:T.mute}}>{MONTH_NAMES[selEv.startMonth]} {selEv.startDay} — {MONTH_NAMES[selEv.endMonth]} {selEv.endDay}, {year}</div>
              {selEv.notes&&<div style={{fontSize:12,color:T.mute,marginTop:8,lineHeight:1.6}}>{selEv.notes}</div>}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setEditingEvent({...selEv})} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"4px 10px",fontSize:11,cursor:"pointer",color:T.mute}}>Düzenle</button>
              {googleToken&&<button onClick={()=>syncToGCal(selEv,googleToken,year).then(()=>{setSyncMsg("Eklendi ✓");setTimeout(()=>setSyncMsg(""),2000);})} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"4px 10px",fontSize:11,cursor:"pointer",color:T.mute}}>→ GCal</button>}
              <button onClick={()=>removeEvent(selEv.id)} style={{background:"none",border:`1px solid ${T.border}`,color:"#c0392b",borderRadius:2,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Konum istatistikleri */}
      <div style={{marginBottom:24}}>
        <div style={{...mono,fontSize:10,color:T.mute,letterSpacing:"0.08em",marginBottom:10}}>{year} — KONUM DAĞILIMI</div>
        <div style={{border:`0.5px solid ${T.border}`,borderRadius:10,padding:"16px 18px"}}>
          <div style={{display:"flex",height:20,borderRadius:5,overflow:"visible",marginBottom:14,position:"relative"}}>
            {cityStats.map(([city,count])=>{
              const pct = (count/total)*100;
              const isSmall = pct<=2;
              return (
                <div key={city} title={`${city}: ${count} gün`} style={{width:`${pct}%`,background:cityColor(city),display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:isSmall?"visible":"hidden",borderRadius:cityStats[0][0]===city?"5px 0 0 5px":cityStats[cityStats.length-1][0]===city?"0 5px 5px 0":0}}>
                  {isSmall?(
                    <span style={{...mono,fontSize:8,color:cityColor(city),fontWeight:600,position:"absolute",top:-14,left:"50%",transform:"translateX(-50%)",whiteSpace:"nowrap"}}>%{Math.round(pct)}</span>
                  ):(
                    <span style={{...mono,fontSize:8,color:"#fff",fontWeight:600}}>%{Math.round(pct)}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:14}}>
            {cityStats.map(([city,count])=>(
              <div key={city} style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{width:8,height:8,borderRadius:2,background:cityColor(city),flexShrink:0}}/>
                <div>
                  <div style={{fontSize:12,color:T.ink,fontWeight:500}}>{city}</div>
                  <div style={{...mono,fontSize:9,color:T.mute}}>{count} gün · %{Math.round((count/total)*100)}</div>
                </div>
              </div>
            ))}
          </div>

          {locations.length>0&&(
            <div style={{marginBottom:10}}>
              {locations.map(loc=>(
                <div key={loc.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",...mono,fontSize:9,color:T.mute}}>
                  <span style={{width:6,height:6,borderRadius:1,background:cityColor(loc.city),flexShrink:0}}/>
                  <span>{loc.city}: {MONTH_NAMES[loc.start_month].slice(0,3)} {loc.start_day} – {MONTH_NAMES[loc.end_month].slice(0,3)} {loc.end_day}</span>
                  <button onClick={()=>removeLocation(loc.id)} style={{background:"none",border:"none",color:"#c0392b",fontSize:12,cursor:"pointer",padding:0,marginLeft:4}}>×</button>
                </div>
              ))}
            </div>
          )}

          {!showLocationForm ? (
            <button onClick={()=>setShowLocationForm(true)} style={{...mono,fontSize:10,background:"none",border:`1px dashed ${T.border}`,borderRadius:6,padding:"5px 12px",cursor:"pointer",color:T.mute}}>+ Konum Ekle</button>
          ) : (
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",paddingTop:8,borderTop:`0.5px solid ${T.borderLight}`}}>
              <select value={newLocation.city} onChange={e=>setNewLocation(f=>({...f,city:e.target.value}))} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                {CITY_PRESETS.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <select value={newLocation.startMonth} onChange={e=>setNewLocation(f=>({...f,startMonth:+e.target.value}))} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 6px",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                {MONTH_NAMES.map((m,i)=><option key={i} value={i}>{m.slice(0,3)}</option>)}
              </select>
              <input type="number" min={1} max={31} value={newLocation.startDay} onChange={e=>setNewLocation(f=>({...f,startDay:+e.target.value}))} style={{width:44,background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 6px",fontSize:11,textAlign:"center",fontFamily:"'Inter',sans-serif"}}/>
              <span style={{...mono,fontSize:10,color:T.mute}}>→</span>
              <select value={newLocation.endMonth} onChange={e=>setNewLocation(f=>({...f,endMonth:+e.target.value}))} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 6px",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                {MONTH_NAMES.map((m,i)=><option key={i} value={i}>{m.slice(0,3)}</option>)}
              </select>
              <input type="number" min={1} max={31} value={newLocation.endDay} onChange={e=>setNewLocation(f=>({...f,endDay:+e.target.value}))} style={{width:44,background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 6px",fontSize:11,textAlign:"center",fontFamily:"'Inter',sans-serif"}}/>
              <button onClick={addLocation} style={{...mono,fontSize:10,background:T.ink,color:"#fff",border:"none",borderRadius:2,padding:"5px 12px",cursor:"pointer"}}>Ekle</button>
              <button onClick={()=>setShowLocationForm(false)} style={{...mono,fontSize:10,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 10px",cursor:"pointer",color:T.mute}}>İptal</button>
            </div>
          )}
        </div>
      </div>

      {/* Not */}
      <div style={{marginBottom:24}}>
        <div style={{...mono,fontSize:10,color:T.mute,letterSpacing:"0.08em",marginBottom:10}}>{year} — NOT</div>
        <div style={{border:`0.5px solid ${T.border}`,borderRadius:10,padding:"14px 16px",background:"#fff"}}>
          <textarea value={yearNote} onChange={e=>saveYearNote(e.target.value)}
            placeholder="Bu yıla dair notlar…"
            style={{width:"100%",background:"transparent",border:"none",padding:0,fontSize:13,fontFamily:"'Inter',sans-serif",resize:"vertical",minHeight:140,lineHeight:1.6,color:T.ink,outline:"none",boxSizing:"border-box"}}/>
        </div>
      </div>
    </div>
  );
}

function EventForm({ form, setForm, onSave, onCancel, title, googleToken }) {
  return (
    <div style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:"20px",marginBottom:24}}>
      <div style={{...mono,fontSize:9,color:T.mute,marginBottom:14}}>{title.toUpperCase()}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Başlık</div>
          <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Memories of Covas — Shoot"
            style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Tür</div>
          <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"7px 10px",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
            {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Ülke</div>
          <input value={form.country||""} onChange={e=>setForm(f=>({...f,country:e.target.value}))} placeholder="Fransa"
            style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"7px 10px",fontSize:12,fontFamily:"'Inter',sans-serif",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Renk</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {["#e4572e","#4a90a4","#9b7ec8","#5a8a5a","#c9843a","#4a6fa5","#3a8a7c","#888","#2d7a2d","#c0392b"].map(c=>(
              <span key={c} onClick={()=>setForm(f=>({...f,color:c}))} style={{width:18,height:18,borderRadius:2,background:c,cursor:"pointer",border:form.color===c?`2px solid ${T.ink}`:"2px solid transparent",flexShrink:0}}/>
            ))}
          </div>
        </div>
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Başlangıç</div>
          <div style={{display:"flex",gap:6}}>
            <select value={form.startMonth} onChange={e=>setForm(f=>({...f,startMonth:+e.target.value}))} style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"6px",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
              {MONTH_NAMES.map((m,i)=><option key={i} value={i}>{m}</option>)}
            </select>
            <input type="number" value={form.startDay} min={1} max={31} onChange={e=>setForm(f=>({...f,startDay:+e.target.value}))} style={{width:46,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"6px",fontSize:12,textAlign:"center",fontFamily:"'Inter',sans-serif"}}/>
          </div>
        </div>
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Bitiş</div>
          <div style={{display:"flex",gap:6}}>
            <select value={form.endMonth} onChange={e=>setForm(f=>({...f,endMonth:+e.target.value}))} style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"6px",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
              {MONTH_NAMES.map((m,i)=><option key={i} value={i}>{m}</option>)}
            </select>
            <input type="number" value={form.endDay} min={1} max={31} onChange={e=>setForm(f=>({...f,endDay:+e.target.value}))} style={{width:46,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"6px",fontSize:12,textAlign:"center",fontFamily:"'Inter',sans-serif"}}/>
          </div>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4}}>Notlar</div>
          <textarea value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2}
            style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"7px 10px",fontSize:12,fontFamily:"'Inter',sans-serif",resize:"none",boxSizing:"border-box"}}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={onSave} style={{background:T.ink,color:"#fff",border:"none",borderRadius:2,padding:"7px 18px",fontSize:12,cursor:"pointer",fontWeight:500}}>Kaydet</button>
        <button onClick={onCancel} style={{background:"none",border:`1px solid ${T.border}`,color:T.mute,borderRadius:2,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>İptal</button>
        {googleToken&&<span style={{...mono,fontSize:9,color:T.mute}}>Kaydedince Google Calendar'a da eklenecek</span>}
      </div>
    </div>
  );
}

/* ── Travel Uploader ── */
function TravelUploader({ apiKey, onSaved }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function parsePDF() {
    if (!file) return;
    if (!apiKey) { setError("Önce API key gir (⚙ ikonu)"); return; }
    setParsing(true); setError(""); setResult(null);

    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = () => rej(new Error("Dosya okunamadı"));
        r.readAsDataURL(file);
      });

      const prompt = `Bu uçak bileti PDF'inden uçuş ve konaklama bilgilerini çıkar.

Şu JSON formatında döndür (başka hiçbir şey yazma):
{
  "flights": [
    {
      "flight_number": "TK123",
      "pnr": "ABC123",
      "airline": "Turkish Airlines",
      "from": "İstanbul (IST)",
      "to": "Berlin (TXL)",
      "date": "2026-05-15",
      "departure_time": "14:30",
      "arrival_time": "17:45",
      "duration": "3s 15dk"
    }
  ],
  "accommodation": [
    {
      "name": "Otel/Airbnb adı",
      "address": "Tam adres",
      "checkin": "2026-05-15",
      "checkout": "2026-05-18"
    }
  ]
}

Birden fazla uçuş olabilir. Konaklama yoksa boş dizi bırak. Tüm alanlar opsiyonel.`;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-5",
          max_tokens: 1500,
          messages: [{
            role: "user",
            content: [
              { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
              { type: "text", text: prompt }
            ]
          }]
        })
      });

      const data = await response.json();
      if (data.error) { setError(data.error.message); setParsing(false); return; }

      const text = data.content?.[0]?.text || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
    } catch(e) {
      setError("PDF okunamadı: " + e.message);
    }
    setParsing(false);
  }

  async function save() {
    if (!result) return;
    const firstFlight = result.flights?.[0];
    const tr = {
      id: uid(),
      pdf_name: file.name,
      flights: result.flights || [],
      accommodation: result.accommodation || [],
      travel_date: firstFlight?.date || "",
    };
    await supabase.from("travels").insert(tr);
    onSaved(tr);
  }

  return (
    <div style={{ background:"transparent", border:`0.5px solid ${T.border}`, borderRadius:8, padding:"18px", marginBottom:20 }}>
      <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:12 }}>PDF YÜKLE</div>

      {!result ? (
        <div>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
            <label style={{ flex:1, display:"flex", alignItems:"center", gap:8, padding:"10px 14px", border:`2px dashed ${T.border}`, borderRadius:3, cursor:"pointer", background:T.bg }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <span style={{ fontSize:20 }}>✈</span>
              <div>
                <div style={{ fontSize:13, color:T.ink, fontWeight:500 }}>{file ? file.name : "Bilet PDF'ini seç"}</div>
                <div style={{ ...mono, fontSize:9, color:T.mute }}>Uçak bileti veya rezervasyon</div>
              </div>
              <input type="file" accept=".pdf" style={{ display:"none" }} onChange={e=>setFile(e.target.files[0])}/>
            </label>
          </div>
          {error&&<div style={{ ...mono, fontSize:9, color:"#c0392b", marginBottom:8 }}>{error}</div>}
          <button onClick={parsePDF} disabled={!file||parsing}
            style={{ background:file&&!parsing?T.ink:"#ccc", color:"#fff", border:"none", borderRadius:2, padding:"8px 18px", fontSize:12, cursor:file&&!parsing?"pointer":"not-allowed", fontWeight:500, width:"100%" }}>
            {parsing ? "AI okuyor…" : "Bilgileri Çıkar"}
          </button>
        </div>
      ) : (
        <div>
          <div style={{ ...mono, fontSize:9, color:"#2d7a2d", marginBottom:10 }}>✓ Bilgiler çıkarıldı</div>
          {result.flights?.map((f,i)=>(
            <div key={i} style={{ padding:"8px 10px", background:T.bg, borderRadius:3, marginBottom:6, fontSize:12 }}>
              <div style={{ fontWeight:600 }}>{f.from} → {f.to}</div>
              <div style={{ ...mono, fontSize:9, color:T.mute }}>{f.date} · {f.departure_time} → {f.arrival_time} · {f.duration}</div>
              {f.pnr&&<div style={{ ...mono, fontSize:9, color:T.accent }}>PNR: {f.pnr}</div>}
            </div>
          ))}
          {result.accommodation?.map((a,i)=>(
            <div key={i} style={{ padding:"8px 10px", background:"#f0f8f0", borderRadius:3, marginBottom:6, fontSize:12 }}>
              <div style={{ fontWeight:600 }}>{a.name}</div>
              {a.address&&<div style={{ ...mono, fontSize:9, color:T.mute }}>{a.address}</div>}
            </div>
          ))}
          <div style={{ display:"flex", gap:8, marginTop:10 }}>
            <button onClick={save} style={{ flex:1, background:"#2d7a2d", color:"#fff", border:"none", borderRadius:2, padding:"8px", fontSize:12, cursor:"pointer", fontWeight:500 }}>Kaydet</button>
            <button onClick={()=>setResult(null)} style={{ background:"none", border:`1px solid ${T.border}`, color:T.mute, borderRadius:2, padding:"8px 14px", fontSize:12, cursor:"pointer" }}>İptal</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ── */
function formatDate(dateStr) {
  if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr||"";
  const [y,m,d] = dateStr.split("-");
  return `${d}.${m}.${y}`;
}

function parseDurationMins(dur) {
  if (!dur) return 0;
  const hm = dur.match(/(\d+)\s*[sh]\s*(?:(\d+)\s*dk?)?/i);
  if (hm) return (parseInt(hm[1])||0)*60+(parseInt(hm[2])||0);
  const m = dur.match(/(\d+)\s*dk?/i);
  return m?parseInt(m[1])||0:0;
}
function formatDuration(mins) {
  const h=Math.floor(mins/60),m=mins%60;
  return m>0?`${h}s ${m}dk`:`${h}s`;
}
function extractIATA(str) {
  if (!str) return "—";
  const m=str.match(/\(([A-Z]{3})\)/);
  return m?m[1]:str.slice(0,3).toUpperCase();
}
function extractCity(str) {
  if (!str) return "";
  return str.replace(/\s*\([A-Z]{3}\)/,"").trim();
}

/* ── Inline edit field ── */
function TravelField({ value, onSave, placeholder="", style={}, type="text" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value||"");
  useEffect(()=>setVal(value||""),[value]);
  const commit = () => { setEditing(false); if(val!==(value||"")) onSave(val); };
  if (editing) return (
    <input type={type} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape"){setVal(value||"");setEditing(false);}}}
      autoFocus
      style={{...style,border:`1px solid #4a6fa5`,borderRadius:2,padding:"1px 5px",outline:"none",fontFamily:"inherit",background:"#fff",minWidth:60}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Düzenlemek için çift tıkla"
      style={{...style,cursor:"text",borderBottom:`1px dashed transparent`}}
      onMouseEnter={e=>e.currentTarget.style.borderBottomColor="#ccc"}
      onMouseLeave={e=>e.currentTarget.style.borderBottomColor="transparent"}>
      {value||<span style={{color:"#ccc",fontStyle:"italic"}}>{placeholder}</span>}
    </span>
  );
}

/* ── Travel Timeline — Kanban ── */
function TravelTimeline({ travels, onDelete }) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0,10);
  const [selected, setSelected] = useState(null);
  const [addingAccomm, setAddingAccomm] = useState(null);
  const [newAccomm, setNewAccomm] = useState({name:"",address:"",checkin:"",checkout:""});
  const [localTravels, setLocalTravels] = useState(travels);

  useEffect(()=>setLocalTravels(travels),[travels]);

  async function updateGmailQuery(travelId, query) {
    setLocalTravels(prev=>prev.map(t=>t.id===travelId?{...t,gmail_query:query}:t));
    await supabase.from("travels").update({gmail_query:query}).eq("id",travelId);
  }

  async function updateFlight(travelId, flightIdx, patch) {
    setLocalTravels(prev=>prev.map(t=>{
      if(t.id!==travelId) return t;
      const flights = [...(t.flights||[])];
      flights[flightIdx] = {...flights[flightIdx], ...patch};
      return {...t, flights};
    }));
    const tr = localTravels.find(t=>t.id===travelId);
    if (!tr) return;
    const flights = [...(tr.flights||[])];
    flights[flightIdx] = {...flights[flightIdx], ...patch};
    await supabase.from("travels").update({flights}).eq("id",travelId);
  }

  async function saveAccomm(travelId) {
    if (!newAccomm.name.trim()) return;
    const tr = localTravels.find(t=>t.id===travelId);
    if (!tr) return;
    const updated = [...(tr.accommodation||[]), {...newAccomm}];
    setLocalTravels(prev=>prev.map(t=>t.id===travelId?{...t,accommodation:updated}:t));
    await supabase.from("travels").update({accommodation:updated}).eq("id",travelId);
    setNewAccomm({name:"",address:"",checkin:"",checkout:""});
    setAddingAccomm(null);
  }

  async function updateAccomm(travelId, idx, patch) {
    const tr = localTravels.find(t=>t.id===travelId);
    if (!tr) return;
    const updated = (tr.accommodation||[]).map((a,i)=>i===idx?{...a,...patch}:a);
    setLocalTravels(prev=>prev.map(t=>t.id===travelId?{...t,accommodation:updated}:t));
    await supabase.from("travels").update({accommodation:updated}).eq("id",travelId);
  }

  async function removeAccomm(travelId, idx) {
    const tr = localTravels.find(t=>t.id===travelId);
    if (!tr) return;
    const updated = (tr.accommodation||[]).filter((_,i)=>i!==idx);
    setLocalTravels(prev=>prev.map(t=>t.id===travelId?{...t,accommodation:updated}:t));
    await supabase.from("travels").update({accommodation:updated}).eq("id",travelId);
  }

  const sorted = [...localTravels].sort((a,b)=>{
    const ad=(a.flights||[])[0]?.date||"9999";
    const bd=(b.flights||[])[0]?.date||"9999";
    return ad.localeCompare(bd);
  });
  const upcoming = sorted.filter(tr=>{const f=(tr.flights||[])[0];return !f?.date||f.date>=todayStr;});
  const past = sorted.filter(tr=>{const f=(tr.flights||[])[0];return f?.date&&f.date<todayStr;}).reverse();

  function TravelKanbanCard({ tr, isPast }) {
    const isSelected = selected===tr.id;
    const flights = tr.flights||[];
    const accomm = tr.accommodation||[];
    const firstFlight = flights[0];
    const lastFlight = flights[flights.length-1];
    const from = extractIATA(firstFlight?.from);
    const to = extractIATA(lastFlight?.to||firstFlight?.to);
    const totalDur = flights.map(f=>parseDurationMins(f.duration)).reduce((a,b)=>a+b,0);

    return (
      <div style={{background:"transparent",border:`0.5px solid ${isSelected?T.accent:T.border}`,borderRadius:8,overflow:"hidden",marginBottom:10,opacity:isPast?0.75:1,transition:"border-color .12s"}}>
        <div style={{height:3,background:isPast?"#ddd":"linear-gradient(90deg,#4a6fa5,#3a8a7c)"}}/>

        <div style={{padding:"8px 14px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}} onClick={e=>e.stopPropagation()}>
          <TravelField value={tr.pdf_name?.replace(".pdf","")||"Seyahat"} onSave={async v=>{
            setLocalTravels(prev=>prev.map(t=>t.id===tr.id?{...t,pdf_name:v}:t));
            await supabase.from("travels").update({pdf_name:v}).eq("id",tr.id);
          }} style={{fontSize:11,fontWeight:500,color:T.mute}} placeholder="Seyahat adı…"/>
        </div>
        <div onClick={()=>setSelected(isSelected?null:tr.id)} style={{padding:"8px 14px 12px",cursor:"pointer"}}>
          {firstFlight?.date&&<div style={{...mono,fontSize:8,color:isPast?T.mute:T.accent,marginBottom:6,fontWeight:600}}>{formatDate(firstFlight.date)}{isPast?" · GEÇTİ":""}</div>}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:20,fontWeight:700,color:T.ink,letterSpacing:"-0.02em"}}>{from}</span>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
              <div style={{width:"100%",height:1,background:`linear-gradient(90deg,${T.border},#4a6fa5,${T.border})`,position:"relative"}}>
                <span style={{position:"absolute",left:"50%",top:-8,transform:"translateX(-50%)",fontSize:12}}>✈</span>
              </div>
              {totalDur>0&&<span style={{...mono,fontSize:8,color:T.mute,marginTop:2}}>{formatDuration(totalDur)}</span>}
            </div>
            <span style={{fontSize:20,fontWeight:700,color:T.ink,letterSpacing:"-0.02em"}}>{to}</span>
          </div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
            {flights.length>1&&<span style={{...mono,fontSize:8,color:T.mute,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"1px 6px"}}>{flights.length} uçuş</span>}
            {firstFlight?.airline&&<span style={{...mono,fontSize:8,color:T.mute,background:T.bg,border:`1px solid ${T.border}`,borderRadius:2,padding:"1px 6px"}}>{firstFlight.airline}</span>}
            {accomm.length>0&&<span style={{...mono,fontSize:8,color:"#3a8a7c",background:"#eaf6f4",border:`1px solid #c0e8e0`,borderRadius:2,padding:"1px 6px"}}>🏨 {accomm.length}</span>}
            {tr.gmail_query&&!isSelected&&(
              <a href={tr.gmail_query} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
                style={{...mono,fontSize:8,color:"#4a6fa5",background:"#eaf0fa",border:"1px solid #a8c8f0",borderRadius:2,padding:"1px 6px",textDecoration:"none",flexShrink:0}}>✉</a>
            )}
            <span style={{...mono,fontSize:8,color:T.mute,marginLeft:"auto",opacity:0.5}}>{isSelected?"▲":"▼"}</span>
          </div>
        </div>

        {isSelected&&(
          <div style={{borderTop:`0.5px solid ${T.border}`,padding:"14px"}}>
            <div style={{...mono,fontSize:8,color:T.mute,marginBottom:8}}>UÇUŞLAR · çift tıkla düzenle</div>

            {flights.map((f,fi)=>(
              <div key={fi} style={{background:"transparent",border:`0.5px solid ${T.borderLight}`,borderRadius:4,padding:"10px 12px",marginBottom:8}}>
                <div style={{...mono,fontSize:8,color:"#4a6fa5",marginBottom:6,fontWeight:600}}>UÇUŞ {fi+1}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:8,alignItems:"center",marginBottom:8}}>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>NEREDEN</div>
                    <TravelField value={f.from} onSave={v=>updateFlight(tr.id,fi,{from:v})} placeholder="İstanbul (IST)" style={{fontSize:13,fontWeight:600,color:T.ink}}/>
                  </div>
                  <span style={{...mono,fontSize:10,color:T.mute}}>→</span>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>NEREYE</div>
                    <TravelField value={f.to} onSave={v=>updateFlight(tr.id,fi,{to:v})} placeholder="Berlin (TXL)" style={{fontSize:13,fontWeight:600,color:T.ink}}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>TARİH</div>
                    <TravelField value={f.date} onSave={v=>updateFlight(tr.id,fi,{date:v})} placeholder="2026-05-15" style={{...mono,fontSize:10}} type="date"/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>KALKIŞ</div>
                    <TravelField value={f.departure_time} onSave={v=>updateFlight(tr.id,fi,{departure_time:v})} placeholder="14:30" style={{fontSize:14,fontWeight:700}} type="time"/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>VARIŞ</div>
                    <TravelField value={f.arrival_time} onSave={v=>updateFlight(tr.id,fi,{arrival_time:v})} placeholder="17:45" style={{fontSize:14,fontWeight:700}} type="time"/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>SÜRE</div>
                    <TravelField value={f.duration} onSave={v=>updateFlight(tr.id,fi,{duration:v})} placeholder="3s 15dk" style={{...mono,fontSize:10,color:"#4a6fa5"}}/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>PNR</div>
                    <TravelField value={f.pnr} onSave={v=>updateFlight(tr.id,fi,{pnr:v})} placeholder="ABC123" style={{...mono,fontSize:10,fontWeight:600,color:"#3a8a7c"}}/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>SEFER</div>
                    <TravelField value={f.flight_number} onSave={v=>updateFlight(tr.id,fi,{flight_number:v})} placeholder="TK123" style={{...mono,fontSize:10}}/>
                  </div>
                  <div>
                    <div style={{...mono,fontSize:7,color:T.mute,marginBottom:2}}>HAVAYOLU</div>
                    <TravelField value={f.airline} onSave={v=>updateFlight(tr.id,fi,{airline:v})} placeholder="Turkish Airlines" style={{fontSize:11}}/>
                  </div>
                </div>
              </div>
            ))}

            <div style={{marginBottom:8}}>
              <div style={{...mono,fontSize:8,color:T.mute,marginBottom:4}}>GMAİL LİNKİ</div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input
                  value={tr.gmail_query||""}
                  onChange={e=>updateGmailQuery(tr.id,e.target.value)}
                  placeholder="Gmail mail linkini yapıştır…"
                  style={{flex:1,background:"transparent",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                {tr.gmail_query&&(
                  <a href={tr.gmail_query} target="_blank" rel="noreferrer"
                    style={{...mono,fontSize:9,background:"#4a6fa5",color:"#fff",border:"none",borderRadius:2,padding:"5px 10px",cursor:"pointer",textDecoration:"none",whiteSpace:"nowrap",flexShrink:0}}>
                    Aç ↗
                  </a>
                )}
              </div>
            </div>

            <div style={{marginTop:4}}>
              <div style={{...mono,fontSize:8,color:T.mute,marginBottom:6}}>KONAKLAMA · çift tıkla düzenle</div>
              {accomm.map((a,ai)=>(
                <div key={ai} style={{background:"#f0faf8",border:`1px solid #c0e8e0`,borderRadius:4,padding:"10px 12px",marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                    <TravelField value={a.name} onSave={v=>updateAccomm(tr.id,ai,{name:v})} placeholder="Otel / Airbnb adı" style={{fontSize:13,fontWeight:500,color:T.ink}}/>
                    <button onClick={()=>removeAccomm(tr.id,ai)} style={{background:"none",border:"none",color:"#c0392b",fontSize:13,cursor:"pointer",padding:0,opacity:0.4}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.4"}>×</button>
                  </div>
                  <TravelField value={a.address} onSave={v=>updateAccomm(tr.id,ai,{address:v})} placeholder="Adres ekle…" style={{fontSize:11,color:T.mute,display:"block",marginBottom:4}}/>
                  {a.address&&(
                    <div style={{marginBottom:6,borderRadius:4,overflow:"hidden",border:`1px solid #c0e8e0`}}>
                      <iframe
                        title="map"
                        width="100%" height="100"
                        style={{border:"none",display:"block"}}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(a.address)}&output=embed`}/>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.address)}`} target="_blank" rel="noreferrer"
                        style={{display:"flex",alignItems:"center",gap:5,padding:"5px 8px",background:"#f0faf8",fontSize:10,color:"#3a8a7c",textDecoration:"none",fontFamily:"'JetBrains Mono',monospace"}}>
                        <span>📍</span> Google Maps'te aç
                      </a>
                    </div>
                  )}
                  <div style={{display:"flex",gap:8}}>
                    <div>
                      <div style={{...mono,fontSize:7,color:T.mute,marginBottom:1}}>GİRİŞ</div>
                      <TravelField value={a.checkin} onSave={v=>updateAccomm(tr.id,ai,{checkin:v})} style={{...mono,fontSize:10,color:"#3a8a7c"}} type="date"/>
                    </div>
                    <div>
                      <div style={{...mono,fontSize:7,color:T.mute,marginBottom:1}}>ÇIKIŞ</div>
                      <TravelField value={a.checkout} onSave={v=>updateAccomm(tr.id,ai,{checkout:v})} style={{...mono,fontSize:10,color:"#3a8a7c"}} type="date"/>
                    </div>
                  </div>
                </div>
              ))}
              {addingAccomm===tr.id?(
                <div style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:4,padding:"10px"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <input value={newAccomm.name} onChange={e=>setNewAccomm(p=>({...p,name:e.target.value}))} placeholder="Otel / Airbnb adı…"
                      style={{border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",background:"transparent"}}/>
                    <input value={newAccomm.address} onChange={e=>setNewAccomm(p=>({...p,address:e.target.value}))} placeholder="Adres…"
                      style={{border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",background:"transparent"}}/>
                    <div style={{display:"flex",gap:6}}>
                      <input type="date" value={newAccomm.checkin} onChange={e=>setNewAccomm(p=>({...p,checkin:e.target.value}))}
                        style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none",background:"transparent"}}/>
                      <input type="date" value={newAccomm.checkout} onChange={e=>setNewAccomm(p=>({...p,checkout:e.target.value}))}
                        style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 8px",fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none",background:"transparent"}}/>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>saveAccomm(tr.id)} style={{flex:1,background:"#3a8a7c",color:"#fff",border:"none",borderRadius:2,padding:"6px",fontSize:11,cursor:"pointer",fontWeight:500}}>Ekle</button>
                      <button onClick={()=>{setAddingAccomm(null);setNewAccomm({name:"",address:"",checkin:"",checkout:""});}} style={{background:"none",border:`1px solid ${T.border}`,color:T.mute,borderRadius:2,padding:"6px 10px",fontSize:11,cursor:"pointer"}}>İptal</button>
                    </div>
                  </div>
                </div>
              ):(
                <button onClick={()=>setAddingAccomm(tr.id)}
                  style={{...mono,fontSize:9,background:"none",border:`1px dashed ${T.border}`,borderRadius:3,padding:"6px 10px",cursor:"pointer",color:T.mute,width:"100%"}}>+ Konaklama Ekle</button>
              )}
            </div>

            <button onClick={()=>{onDelete(tr.id);setSelected(null);}}
              style={{...mono,fontSize:8,background:"none",border:`1px solid #fcc`,color:"#c0392b",borderRadius:2,padding:"5px",cursor:"pointer",width:"100%",marginTop:8}}>Seyahati Sil</button>
          </div>
        )}
      </div>
    );
  }

  if (localTravels.length===0) return (
    <div style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:"32px",marginBottom:24,textAlign:"center"}}>
      <div style={{fontSize:32,marginBottom:8}}>✈</div>
      <div style={{...mono,fontSize:10,color:T.mute}}>Henüz seyahat yok. Üstteki "PDF Yükle" butonundan ekle.</div>
    </div>
  );

  return (
    <div style={{marginBottom:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Seyahatler</div>
        <span style={{...mono,fontSize:9,color:T.mute}}>{localTravels.length} seyahat</span>
      </div>
      {upcoming.length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:8,letterSpacing:"0.1em"}}>YAKLAŞAN</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
            {upcoming.map(tr=><TravelKanbanCard key={tr.id} tr={tr} isPast={false}/>)}
          </div>
        </div>
      )}
      {past.length>0&&(
        <div>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:8,letterSpacing:"0.1em"}}>GEÇMİŞ</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
            {past.map(tr=><TravelKanbanCard key={tr.id} tr={tr} isPast={true}/>)}
          </div>
        </div>
      )}
    </div>
  );
}
